import React, {useMemo} from 'react';
import { Checkbox, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {BulkActionsTableProps} from './BulkActionsTable.props';
import {INITIAL, PENDING, POLLING} from '../../../../../../hooks/useRollingApiRequest/useRollingApiRequest.props';
import {typedObjectValues} from '../../../../../../utilities';
import {columnHeaders} from '../../constants';
import ResultStatusIcon from '../ResultStatusIcon/ResultStatusIcon';

const BulkActionsTable = ({
  selectionModelState: {
    value: selectionModel,
    setter: setSelectionModel
  },
  rows,
  visibleColumns,
  saveStatus,
  saveResults,
  defaultRowConfig,
}: BulkActionsTableProps) => {

  const hasInitializedUpdate = useMemo(() => saveStatus !== INITIAL, [saveStatus])

  const fullfilledPromises = useMemo(() => {
    const listSaveResults = typedObjectValues(saveResults)
    const completedResults = listSaveResults.filter(data => ![PENDING, POLLING].includes(data.state))
    return `${completedResults.length}/${listSaveResults.length}`
  }, [saveResults])

  return (
    <TableContainer style={{ maxHeight: 400, overflow: 'auto' }}>
      <Table >
        <TableHead>
          <TableRow>
            <TableCell />
            {defaultRowConfig.filter(column => column.isVisible).map(column => (
              <TableCell key={column.field}>
                {column.renderCell()}
              </TableCell>
            ))}
            {hasInitializedUpdate && (<TableCell>
              <Chip label={fullfilledPromises} color="primary"/>
            </TableCell>)}
          </TableRow>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={selectionModel.length > 0 && selectionModel.length < rows.length}
                checked={rows.length > 0 && selectionModel.length === rows.length}
                onChange={event => {
                  if (event.target.checked) {
                    const newSelectionModel = rows.map(row => row.id);
                    setSelectionModel(newSelectionModel);
                  } else {
                    setSelectionModel([]);
                  }
                }}
              />
            </TableCell>
            {columnHeaders
              .filter(columnHeader => visibleColumns.includes(columnHeader.field))
              .map(column => {
                if (column.subColumns.length > 0) {
                  return column.subColumns.map(subColumn => (
                    <TableCell key={`${column.field}-${subColumn.field}`}>
                      <strong>{subColumn.headerName}</strong>
                    </TableCell>
                  ))
                }
                return (
                  <TableCell key={column.field}>
                    <strong>{column.headerName}</strong>
                  </TableCell>
                );
              })}
            {hasInitializedUpdate && (<TableCell/>)}
          </TableRow>
        </TableHead>
        <TableBody  style={{ maxHeight: 400, overflow: 'auto' }}>
          {rows.toSorted((a, b) => a.company_name?.localeCompare(b.company_name || '') || 0).map(row => (
            <TableRow
              key={row.id}
              hover
              selected={selectionModel.includes(row.id)}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectionModel.includes(row.id)}
                  onChange={event => {
                    const newSelectionModel = event.target.checked
                      ? [...selectionModel, row.id]
                      : selectionModel.filter(id => id !== row.id);
                    setSelectionModel(newSelectionModel);
                  }}
                />
              </TableCell>
              <TableCell>
                {row.company_name}
              </TableCell>
              {row.rowCells.filter(rowCell => rowCell.isVisible).map(rowCell => (
                <TableCell key={`${row.id}-${rowCell.field}`}>
                  {rowCell.renderCell()}
                </TableCell>
              ))}
              {hasInitializedUpdate && (<TableCell>
                {saveResults[row.valuation_id] && (
                  <ResultStatusIcon state={saveResults[row.valuation_id].state} errorMsg={saveResults[row.valuation_id].errorMsg}/>
                )}
              </TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default BulkActionsTable;